import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Gavel, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { TENDERS } from "@/constants/testIds";
import ExportBar from "@/components/ExportBar";

const STATUS_COLORS = {
    Open: "#3B82F6",
    "Under Evaluation": "#D4AF37",
    Closed: "#94A3B8",
    Draft: "#64748B",
    Awarded: "#10B981",
};

function fmtMoney(n) {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
    return `$${n}`;
}

export default function Tenders() {
    const [tenders, setTenders] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [aiMap, setAiMap] = useState({});
    const [busy, setBusy] = useState(null);

    useEffect(() => { api.get("/tenders").then((r) => setTenders(r.data)); }, []);

    const analyze = async (t) => {
        setBusy(t.id);
        try {
            const { data } = await api.post("/ai/task", {
                kind: "tender_analysis",
                payload: { tender_ref: t.ref },
            });
            setAiMap((m) => ({ ...m, [t.id]: data.result }));
            setExpanded(t.id);
            toast.success(`Analysis ready for ${t.ref}`);
        } catch {
            toast.error("AI tender analysis failed");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <div className="kicker text-[var(--sgsb-gold)]">Procurement</div>
                <h1 className="mt-2 font-display font-black text-white text-4xl tracking-tighter">Tender Management</h1>
                <p className="mt-2 text-slate-400 text-sm font-body max-w-2xl">
                    Pipeline of open, under-evaluation and awarded tenders. Run GPT-5.2 bid analysis on any tender.
                </p>
            </div>

            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60 flex items-center justify-between">
                    <h3 className="font-display font-black text-white tracking-tight flex items-center gap-2">
                        <Gavel className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        Tender pipeline
                    </h3>
                    <span className="kicker text-slate-500 text-[10px]">{tenders.length} tenders</span>
                </div>
                <div className="divide-y divide-slate-800/60">
                    {tenders.map((t) => (
                        <div key={t.id} data-testid={TENDERS.row(t.id)} className="p-5 hover:bg-slate-900/30 transition-colors duration-150">
                            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_auto_auto] gap-4 items-center">
                                <div>
                                    <div className="kicker text-slate-500 text-[10px]">{t.ref}</div>
                                    <div className="mt-0.5 font-display font-black text-white tracking-tight">{t.title}</div>
                                    <div className="text-[11px] text-slate-500 font-body mt-0.5">
                                        {t.project_code} · Closes {t.close_date} · {t.bidder_count} bidders
                                    </div>
                                </div>
                                <div>
                                    <span
                                        className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px]"
                                        style={{
                                            background: `${STATUS_COLORS[t.status]}22`,
                                            color: STATUS_COLORS[t.status],
                                            border: `1px solid ${STATUS_COLORS[t.status]}55`,
                                        }}
                                    >
                                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_COLORS[t.status] }} />
                                        {t.status}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <div className="kicker text-slate-500 text-[10px]">Estimate</div>
                                    <div className="font-display font-black text-white">{fmtMoney(t.estimate_usd)}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        data-testid={TENDERS.analyzeBtn(t.id)}
                                        onClick={() => analyze(t)}
                                        disabled={busy === t.id}
                                        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-3 py-1.5 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                                        {busy === t.id ? "Analysing…" : "AI Analyse"}
                                    </button>
                                    <button
                                        onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800/60"
                                    >
                                        {expanded === t.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            {expanded === t.id && (
                                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className="rounded-md bg-slate-950/60 border border-slate-800 p-4">
                                        <div className="kicker text-slate-500 text-[10px] mb-2">Bidders</div>
                                        {t.bidders && t.bidders.length ? (
                                            <table className="w-full text-xs font-body">
                                                <thead>
                                                    <tr className="text-slate-500">
                                                        <th className="text-left py-1">Bidder</th>
                                                        <th className="text-right py-1">Bid</th>
                                                        <th className="text-right py-1">Score</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {t.bidders.map((b, i) => (
                                                        <tr key={i} className="border-t border-slate-800">
                                                            <td className="py-2 text-slate-200">{b.name}</td>
                                                            <td className="py-2 text-right text-slate-300">{fmtMoney(b.bid_usd)}</td>
                                                            <td className="py-2 text-right text-[var(--sgsb-gold)]">{b.score}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="text-xs text-slate-500 font-body">No bidders yet.</div>
                                        )}
                                    </div>
                                    <div className="rounded-md bg-slate-950/60 border border-slate-800 p-4">
                                        <div className="kicker text-[var(--sgsb-gold)] text-[10px] mb-2">AI Evaluation · GPT-5.2</div>
                                        <pre className="whitespace-pre-wrap text-slate-200 font-body text-xs leading-relaxed">
                                            {aiMap[t.id] || "Run AI Analyse to generate an evaluation."}
                                        </pre>
                                        {aiMap[t.id] && (
                                            <ExportBar
                                                title={`Tender Evaluation — ${t.ref}`}
                                                content={aiMap[t.id]}
                                                confidentiality="Restricted"
                                                testIdPrefix={`tender-export-${t.id}`}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
